import { useState } from 'react';
import axios from 'axios';
import { FileText, Download, Mail, Loader2, History, Edit, Save, X } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import PinModal from './PinModal';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PatientHistoryExport = ({ patient, isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [exportData, setExportData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [editingSummary, setEditingSummary] = useState(false);
  const [summaryContent, setSummaryContent] = useState('');
  const [versionNote, setVersionNote] = useState('');
  const [showPinModal, setShowPinModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleExport = async (pin) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/patients/${patient.id}/history-export?pin=${pin}&include_encounters=20`
      );
      setExportData(response.data);
      toast.success('Patient history exported');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export history');
    } finally {
      setLoading(false);
      setShowPinModal(false);
    }
  };

  const handleGetSummary = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/patients/${patient.id}/auto-summary`);
      setSummary(response.data);
      setSummaryContent(response.data.content);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get summary');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSummary = async (pin) => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/patients/${patient.id}/summary?pin=${pin}`,
        {
          content: summaryContent,
          version_note: versionNote
        }
      );
      toast.success('Summary saved');
      setEditingSummary(false);
      setVersionNote('');
      // Refresh summary
      const response = await axios.get(`${API}/patients/${patient.id}/auto-summary`);
      setSummary(response.data);
      setSummaryContent(response.data.content);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save summary');
    } finally {
      setSaving(false);
      setShowPinModal(false);
    }
  };

  const handlePinVerify = (pin) => {
    if (pendingAction === 'export') {
      handleExport(pin);
    } else if (pendingAction === 'save') {
      handleSaveSummary(pin);
    }
  };

  const startExport = () => {
    setPendingAction('export');
    setShowPinModal(true);
  };

  const startSave = () => {
    setPendingAction('save');
    setShowPinModal(true);
  };

  const downloadPDF = () => {
    if (!exportData) return;
    
    // Create print-friendly HTML
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Patient History - ${patient.full_name}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
          h1 { color: #1e3a5f; border-bottom: 2px solid #1e3a5f; padding-bottom: 10px; }
          h2 { color: #334155; margin-top: 20px; border-bottom: 1px solid #e2e8f0; }
          .section { margin-bottom: 20px; }
          .label { font-weight: bold; color: #475569; }
          .value { color: #1e293b; }
          table { width: 100%; border-collapse: collapse; margin: 10px 0; }
          th, td { border: 1px solid #e2e8f0; padding: 8px; text-align: left; }
          th { background: #f8fafc; }
          .footer { margin-top: 30px; padding-top: 10px; border-top: 1px solid #e2e8f0; font-size: 12px; color: #64748b; }
        </style>
      </head>
      <body>
        <h1>Patient History Report</h1>
        <p class="footer">Generated: ${new Date().toLocaleString()} by ${exportData.exported_by}</p>
        
        <h2>Patient Information</h2>
        <div class="section">
          <p><span class="label">Name:</span> <span class="value">${exportData.patient.full_name}</span></p>
          <p><span class="label">Date of Birth:</span> <span class="value">${exportData.patient.date_of_birth}</span></p>
          <p><span class="label">Gender:</span> <span class="value">${exportData.patient.gender}</span></p>
          <p><span class="label">Phone:</span> <span class="value">${exportData.patient.phone || 'Not provided'}</span></p>
          <p><span class="label">Email:</span> <span class="value">${exportData.patient.email || 'Not provided'}</span></p>
          <p><span class="label">Address:</span> <span class="value">${exportData.patient.address || 'Not provided'}</span></p>
        </div>
        
        <h2>Clinical Summary</h2>
        <div class="section">
          <p><span class="label">Chronic Illnesses:</span> <span class="value">${exportData.patient.chronic_illnesses || 'None documented'}</span></p>
          <p><span class="label">Current Medications:</span> <span class="value">${exportData.patient.current_medications || 'None documented'}</span></p>
          <p><span class="label">Allergies:</span> <span class="value">${exportData.patient.allergies || 'None documented'}</span></p>
        </div>
        
        ${exportData.summary ? `
        <h2>Patient Summary</h2>
        <div class="section">
          <pre style="white-space: pre-wrap; font-family: inherit;">${exportData.summary.content}</pre>
        </div>
        ` : ''}
        
        <h2>Recent Encounters (${exportData.encounters.length})</h2>
        <table>
          <tr>
            <th>Date</th>
            <th>Presenting Complaint</th>
            <th>Assessment</th>
            <th>Status</th>
          </tr>
          ${exportData.encounters.map(enc => `
            <tr>
              <td>${enc.date}</td>
              <td>${(enc.presenting_complaint || '').substring(0, 100)}...</td>
              <td>${(enc.assessment || '').substring(0, 100)}...</td>
              <td>${enc.signed_at ? 'Signed' : 'Draft'}</td>
            </tr>
          `).join('')}
        </table>
        
        <h2>Billing History (${exportData.invoices.length})</h2>
        <table>
          <tr>
            <th>Invoice #</th>
            <th>Date</th>
            <th>Total</th>
            <th>Status</th>
          </tr>
          ${exportData.invoices.map(inv => `
            <tr>
              <td>${inv.invoice_number}</td>
              <td>${inv.created_at?.split('T')[0]}</td>
              <td>$${inv.total?.toFixed(2)}</td>
              <td>${inv.status}</td>
            </tr>
          `).join('')}
        </table>
        
        <p class="footer">
          This document contains confidential medical information. 
          Unauthorized disclosure is prohibited.
        </p>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Patient History Export - {patient?.full_name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Export Section */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-900 mb-2">Export Complete History</h3>
              <p className="text-sm text-blue-700 mb-4">
                Exports demographics, clinical summary, encounters, invoices, and documents.
                Requires Admin PIN.
              </p>
              <div className="flex gap-3">
                <Button onClick={startExport} disabled={loading}>
                  {loading && pendingAction === 'export' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Export History
                </Button>
                {exportData && (
                  <Button variant="outline" onClick={downloadPDF}>
                    <FileText className="w-4 h-4 mr-2" />
                    Download/Print PDF
                  </Button>
                )}
              </div>
            </div>

            {/* Summary Section */}
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-slate-900">Patient Summary</h3>
                <div className="flex gap-2">
                  {!summary && (
                    <Button size="sm" variant="outline" onClick={handleGetSummary} disabled={loading}>
                      Generate Summary
                    </Button>
                  )}
                  {summary && !editingSummary && (
                    <Button size="sm" variant="outline" onClick={() => setEditingSummary(true)}>
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  )}
                </div>
              </div>

              {summary && (
                <div className="space-y-3">
                  {summary.version > 0 && (
                    <p className="text-xs text-slate-500">
                      Version {summary.version} • Last updated by {summary.updated_by_name || summary.created_by_name}
                    </p>
                  )}
                  
                  {editingSummary ? (
                    <div className="space-y-3">
                      <Textarea
                        value={summaryContent}
                        onChange={(e) => setSummaryContent(e.target.value)}
                        rows={15}
                        className="font-mono text-sm"
                      />
                      <div>
                        <label className="text-sm text-slate-600">Version Note (optional)</label>
                        <input
                          type="text"
                          value={versionNote}
                          onChange={(e) => setVersionNote(e.target.value)}
                          placeholder="e.g., Updated chronic conditions"
                          className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={startSave} disabled={saving}>
                          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                          Save (PIN Required)
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => {
                          setEditingSummary(false);
                          setSummaryContent(summary.content);
                        }}>
                          <X className="w-4 h-4 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <pre className="text-sm text-slate-700 whitespace-pre-wrap bg-white p-4 rounded border max-h-80 overflow-y-auto">
                      {summary.content}
                    </pre>
                  )}
                </div>
              )}
            </div>

            {/* Export Preview */}
            {exportData && (
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-100 px-4 py-2 border-b">
                  <h3 className="font-semibold text-slate-800">Export Preview</h3>
                </div>
                <div className="p-4 space-y-4 max-h-60 overflow-y-auto">
                  <div>
                    <p className="text-sm font-medium text-slate-600">Encounters: {exportData.encounters.length}</p>
                    <p className="text-sm font-medium text-slate-600">Invoices: {exportData.invoices.length}</p>
                    <p className="text-sm font-medium text-slate-600">Documents: {exportData.documents.length}</p>
                    <p className="text-sm font-medium text-slate-600">Insurance Records: {exportData.insurance.length}</p>
                  </div>
                  <p className="text-xs text-slate-500">
                    Exported: {new Date(exportData.exported_at).toLocaleString()} by {exportData.exported_by}
                  </p>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <PinModal
        isOpen={showPinModal}
        onClose={() => setShowPinModal(false)}
        onVerify={handlePinVerify}
        title={pendingAction === 'export' ? 'Export Patient History' : 'Save Patient Summary'}
        description={pendingAction === 'export' 
          ? 'Enter your PIN to export patient history' 
          : 'Enter your PIN to save the summary'}
      />
    </>
  );
};

export default PatientHistoryExport;
